import { FormField } from "@/hooks/schema/use-schema";
import { capitalizeFirstLetter, ensureFirstLetterLowercase } from "@/lib/utils";
import { getDocumentPartForMutation, getDocumentPartForQuery } from "./schema.service";
import { FilterOperatorsType } from "@/hooks/use-filter";
import { createGraphQLClient } from "./graphql.service";
import { gql } from "graphql-request";
import { buildNestedDataFieldsForRelations, paginationDataStr } from "@repo/utils/utils";
import { useEntityStore } from "@/stores/entity.store";

export interface EntitySchema {
  fields: Array<FormField>;
  name: string;
}

export interface EntityInstance {
  id: string;
  [field: string]: unknown;
}

export interface ItemsToConnect {
  [fieldName: string]: Array<EntityInstance>;
}
export interface ItemsToDisconnect {
  [fieldName: string]: Array<EntityInstance>;
}
export interface ItemsToSet {
  // `null` means the user cleared a to-one relation; `[]` clears a to-many one.
  [fieldName: string]: Array<EntityInstance> | EntityInstance | null;
}

export interface FilterInput {
  sortby?: Array<SortInput>;
  query?: Array<QueryInput>;
}

export interface QueryInput {
  and?: Array<QueryInput>;
  condition?: {
    field: string;
    operator: FilterOperatorsType;
    value?: string;
    values?: Array<string>;
  };
  or?: Array<QueryInput>;
}

export interface PaginationData {
  current_page: number;
  has_more_pages: boolean;
  per_page: number;
  total: number;
  last_page: number;
}

export interface PaginationInput {
  page: number;
  per_page: number;
}

export interface SortInput {
  field: string;
  order: "asc" | "desc";
}

export const getEntityInstanceList = async ({
  entityName,
  cursor,
  entitySchema,
  filter,
  paginationVariables = {},
  fieldsToFetchForRelationFieldNames,
  fetchRelations = true,
  fieldsToFetch,
}: {
  entityName: string;
  cursor: number;
  entitySchema?: EntitySchema;
  filter?: FilterInput;
  paginationVariables?: object;
  // This should be part of the fiels to fetch separated by .notation
  fieldsToFetchForRelationFieldNames?: Array<{
    fieldName: string;
    fieldsToFetch: Array<string>;
  }>;
  fetchRelations: boolean;
  fieldsToFetch?: Array<string>;
}) => {
  try {
    const lowerCaseEntityName = ensureFirstLetterLowercase(entityName);

    if (!entitySchema) {
      throw Error("no entity schema");
    }

    const { documentString: queryDocument } = await getDocumentPartForQuery({
      entitySchema,
      documentType: { type: "QueryList" },
      fetchRelations,
      fieldsToFetchForRelationFieldNames,
      fieldsToFetch,
      cursor,
    });

    const client = createGraphQLClient({
      endpointName: `${entityName}List`,
    });

    const entityInstances = (await client.request(queryDocument, {
      filter,
      ...paginationVariables,
    })) as {
      [key: typeof lowerCaseEntityName]: {
        data: Array<unknown>;
        pagination: { current_page: number; has_more_pages: boolean };
      };
    };

    const entityInstancesData = entityInstances[`${lowerCaseEntityName}List`].data;

    if (!entityInstancesData) {
      throw new Error("no entity instances found");
    }

    return {
      entityInstances: entityInstancesData || [],
      pagination: entityInstances[`${lowerCaseEntityName}List`]?.pagination as PaginationData,
    };
  } catch (error) {
    console.error("error getting entity instance list", error);
    throw error;
  }
};

export const getEntityInstanceById = async ({
  entityName,
  id,
  entitySchema,
  fieldsToFetchForRelationFieldNames,
  variables,
  fieldsToFetch,
}: {
  id: string;
  entityName: string;
  entitySchema?: EntitySchema;
  variables?: object;
  fieldsToFetchForRelationFieldNames?: Array<{
    fieldName: string;
    fieldsToFetch: Array<string>;
  }>;
  fieldsToFetch?: Array<string>;
}) => {
  try {
    const lowerCaseEntityName = ensureFirstLetterLowercase(entityName);

    const endpointName = `${lowerCaseEntityName}ById`;

    if (!entitySchema) {
      throw Error("no entity schema provided");
    }

    const { documentString: queryDocument } = await getDocumentPartForQuery({
      entitySchema: entitySchema,
      documentType: {
        type: "QueryById",
        id,
      },
      fetchRelations: false,
      fieldsToFetch,
      fieldsToFetchForRelationFieldNames,
    });

    if (!queryDocument) {
      console.error("error getting query document for getEntityInstanceById");
      throw new Error("error getting query document for getEntityInstanceById");
    }

    const client = createGraphQLClient({
      endpointName: `${lowerCaseEntityName}ById`,
    });

    const entityInstances = (await client.request(queryDocument, {
      id,
      ...variables,
    })) as {
      [key: typeof lowerCaseEntityName]: { data: Array<object> };
    };

    if (!entityInstances) {
      console.error("did not find an instance for id", id);
      throw new Error("did not find an instance for id");
    }

    return {
      entityInstance: entityInstances[endpointName].data, //FIXME: reduce it to only the data  without the other stuff
    };
  } catch (error) {
    console.error("error getting entity instance by id", error);
    throw error;
  }
};

export const getEntityInstanceByIdNew = async ({
  entityName,
  id,
  fieldsToFetch = [],
}: {
  entityName: string;
  id: string;
  fieldsToFetch?: string[];
}) => {
  const lowerCaseEntityName = ensureFirstLetterLowercase(entityName);
  const endpointName = `${lowerCaseEntityName}ById`;

  const selectionSet = buildNestedDataFieldsForRelations(fieldsToFetch);

  const QueryDocument = gql`
      query ${lowerCaseEntityName}ById($id: ID!) {
        ${lowerCaseEntityName}ById(id: $id) {
          data {
            ${selectionSet}
          }
        }
      }
    `;

  const client = createGraphQLClient();
  const entityInstance = (await client.request(QueryDocument, {
    id,
  })) as {
    [key: typeof lowerCaseEntityName]: { data: object };
  };

  return {
    data: entityInstance[endpointName].data,
    entityName: entityName,
    id: id,
  };
};

export const getFieldRelationListForEntityInstanceId = async ({
  entityName,
  id,
  fieldsToFetchForRelationFieldNames,
  pagination,
  fieldName,
}: {
  id: string;
  entityName: string;
  pagination: PaginationInput;
  fieldsToFetchForRelationFieldNames?: Array<{
    fieldName: string;
    fieldsToFetch: Array<string>;
  }>;
  fieldName: string;
}) => {
  const lowerCaseEntityName = ensureFirstLetterLowercase(entityName);
  const endpointName = `${lowerCaseEntityName}ById`;

  const relationFieldPaths =
    fieldsToFetchForRelationFieldNames?.find((x) => x.fieldName === fieldName)?.fieldsToFetch ?? [];

  const selectionSetForRelationFieldNames = buildNestedDataFieldsForRelations(
    relationFieldPaths ?? [],
  );

  const field = useEntityStore.getState().getFieldOrRelationByPath(`${entityName}.${fieldName}`);

  // if the relation type ends with one its not a list field
  const isListField = field && "type" in field && field?.type.endsWith("Many");

  const QueryDocument = gql`
      query ${lowerCaseEntityName}ById($id: ID!, ${isListField ? "$pagination: PaginationInput" : ""}) {
        ${lowerCaseEntityName}ById(id: $id) {
          data {
            id
            integer_id
            ${isListField ? `${fieldName}(pagination: $pagination)` : fieldName} {
              data { 
                ${selectionSetForRelationFieldNames}
              }
              ${
                isListField
                  ? `pagination {
                current_page
                has_more_pages
                per_page
                total
                last_page
              }`
                  : ""
              }
            }

          }
        }
      }
    `;

  const client = createGraphQLClient();
  const entityInstances = (await client.request(QueryDocument, {
    id,
    pagination,
  })) as {
    [key: typeof lowerCaseEntityName]: { data: object };
  };

  // Normalize null relation to empty list to avoid UI/cache inconsistencies
  const container = entityInstances[endpointName] as {
    data: {
      [key: string]: {
        data: object[];
        pagination: PaginationData;
      };
    };
  };
  const relationBlock = container?.data?.[fieldName];
  if (!relationBlock) {
    return {
      data: [],
      pagination: {
        current_page: 1,
        has_more_pages: false,
        per_page: 0,
        total: 0,
        last_page: 1,
      } as PaginationData,
    };
  }

  return {
    data: relationBlock.data,
    pagination: relationBlock.pagination,
  } as { data: object[]; pagination?: PaginationData };
};

export const deleteEntityInstance = async ({
  entityName,
  ids,
  entitySchema,
  dontTriggerFlows,
}: {
  entityName: string;
  ids: Array<string>;
  entitySchema?: EntitySchema;
  dontTriggerFlows?: boolean;
}): Promise<{ success: boolean }> => {
  try {
    if (!entitySchema) {
      throw new Error("no entity schema provided");
    }

    const mutationDocument = await getDocumentPartForMutation({
      entitySchema,
      documentType: {
        type: "MutationDelete",
      },
    });

    if (!mutationDocument) {
      console.error("error getting mutation document for deleteEntityInstance");
      throw new Error("error getting mutation document for deleteEntityInstance");
    }

    const client = createGraphQLClient({ dontTriggerFlows });

    const entityInstances = (await client.request(mutationDocument, {
      ids,
    })) as {
      [key: typeof entityName]: { data: Array<unknown> };
    };

    if (!entityInstances) {
      console.error("there was an error deleting entity instance for entity", entityName);
      throw new Error("there was an error deleting entity instance for entity");
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error("error deleting entity instance", error);
    throw error;
  }
};

export const upsertEntityInstance = async ({
  entityName,
  data,
  dontTriggerFlows,
}: {
  entityName: string;
  data: object;
  dontTriggerFlows: boolean;
}) => {
  const client = createGraphQLClient({
    dontTriggerFlows,
    endpointName: `${entityName}Upsert`,
  });

  const isUpdate = "id" in data;

  const upsertMutationDocument = gql`
  mutation ${isUpdate ? "update" : "create"}${capitalizeFirstLetter(entityName)}($input: ${isUpdate ? "Update" : "Create"}${capitalizeFirstLetter(entityName)}Input!) {
    ${isUpdate ? "update" : "create"}${capitalizeFirstLetter(entityName)}(input: $input) {
      data {
        id
      }
    }
  }
  `;

  const updateEntityInstance = (await client.request(upsertMutationDocument, {
    input: data,
  })) as {
    [key: typeof entityName]: { data: { id: string } };
  };

  return {
    data: updateEntityInstance[
      `${isUpdate ? "update" : "create"}${capitalizeFirstLetter(entityName)}`
    ]?.data,
    entityInstanceId:
      updateEntityInstance[`${isUpdate ? "update" : "create"}${capitalizeFirstLetter(entityName)}`]
        ?.data.id,
    entityName,
  };
};

// basic version without relations and pagination for relations
export const getEntityInstanceListNew = async <T = object>({
  entityName,
  paginationInput = { page: 1, per_page: 20 },
  filter = {},
  fieldsToFetch,
  endpointName,
  operationName,
}: {
  entityName: string;
  paginationInput?: PaginationInput;
  filter?: FilterInput;
  fieldsToFetch?: Array<string>;
  endpointName?: string;
  operationName?: string;
}): Promise<{
  entityInstances: Array<T>;
  pagination: PaginationData;
}> => {
  try {
    const lowerCaseEntityName = ensureFirstLetterLowercase(entityName);

    const selectionSet = buildNestedDataFieldsForRelations(fieldsToFetch);

    const queryOperationName = operationName ?? `${lowerCaseEntityName}List`;

    const queryDocument = gql`
      query ${queryOperationName}($filter: FilterInput!, $pagination: PaginationInput!) {
        ${lowerCaseEntityName}List(filter: $filter, pagination: $pagination) {
          data {
            ${selectionSet}
          }
          ${paginationDataStr}
        }
      }
    `;

    const client = createGraphQLClient({
      endpointName: endpointName ?? `${lowerCaseEntityName}List`,
    });

    const entityInstances = (await client.request(queryDocument, {
      filter,
      pagination: paginationInput,
    })) as {
      [key: typeof lowerCaseEntityName]: {
        data: Array<T>;
        pagination: PaginationData;
      };
    };

    const entityInstancesData = entityInstances[`${lowerCaseEntityName}List`].data;

    if (!entityInstancesData) {
      throw new Error("no entity instances found");
    }

    return {
      entityInstances: entityInstancesData || [],
      pagination: entityInstances[`${lowerCaseEntityName}List`]?.pagination as PaginationData,
    };
  } catch (error) {
    console.error("error getting entity instance list", error);
    throw error;
  }
};
